import React, { useState, useEffect, useMemo } from 'react';
import { adminAnalyticsAPI } from '../api/userAPI';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './admin.css';

const APIUsageAnalytics = () => {
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateFilter, setDateFilter] = useState('30'); // '7', '30', '90', 'all', or custom
    const [customDays, setCustomDays] = useState(''); // Used if dateFilter is 'custom'
    const [planTypeFilter, setPlanTypeFilter] = useState('all'); // 'all', 'free', 'basic', 'professional', 'enterprise'
    const [endpointCategoryFilter, setEndpointCategoryFilter] = useState('all'); // 'all', 'models', 'datasets', 'predictions', 'training'
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    
    const loadUsageData = async () => {
        try {
            setLoading(true);
            setError(null);
            const filters = {
                days: dateFilter === "custom" ? customDays : dateFilter,
                plan: planTypeFilter,
                category: endpointCategoryFilter
            };
            const data = await adminAnalyticsAPI.getAPIUsageAnalytics(filters);
            setUsageData(data);
        }
        catch (err) {
            setError('Failed to load API usage analytics.');
        }
        finally {
            setLoading(false);
        }
    }
    
    useEffect(() => {
        loadUsageData();
    }, [dateFilter, planTypeFilter, endpointCategoryFilter]);

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US').format(num);
    };

    // Parse as UTC date
    const formatDate = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatMonth = (monthString) => {
        const [year, month] = monthString.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, 1));
        return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' });
    };

    // Filter users by plan type
    const filteredUsers = useMemo(() => {
        if (!usageData?.by_user) return [];
        if (planTypeFilter === 'all') return usageData.by_user;
        return usageData.by_user.filter(u => u.subscription_tier.toLowerCase() === planTypeFilter);
    }, [usageData?.by_user, planTypeFilter]);

    // Filter daily data based on date filter
    const filteredDailyData = useMemo(() => {
        if (!usageData?.daily || usageData.daily.length === 0) return [];
        const sorted = [...usageData.daily].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        if (dateFilter === 'all') return sorted;
        
        const days = parseInt(dateFilter);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        
        return sorted.filter(d => new Date(d.date) >= cutoffDate);
    }, [usageData?.daily, dateFilter]);

    // Prepare chart data
    const dailyChartData = useMemo(() => {
        return filteredDailyData.map(d => ({
            date: formatDate(d.date),
            calls: d.api_calls,
            fullDate: d.date
        }));
    }, [filteredDailyData]);

    const monthlyChartData = useMemo(() => {
        if (!usageData?.monthly || usageData.monthly.length === 0) return [];
        return [...usageData.monthly]
            .sort((a, b) => a.month.localeCompare(b.month))
            .map(m => ({
                month: formatMonth(m.month),
                calls: m.api_calls,
                fullMonth: m.month
            }));
    }, [usageData?.monthly]);

    const tierChartData = useMemo(() => {
        if (!usageData?.tier_breakdown) return [];
        return Object.entries(usageData.tier_breakdown).map(([tier, count]) => ({
            name: tier.charAt(0).toUpperCase() + tier.slice(1),
            value: count,
            tier: tier.toLowerCase()
        })).sort((a, b) => b.value - a.value);
    }, [usageData?.tier_breakdown]);

    if (loading) {
        return (
            <div className="api-usage-analytics">
                <div className="loading">Loading API usage analytics...</div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="api-usage-analytics">
                <div className="error">
                    <h3>Error Loading Analytics</h3>
                    <p>{error}</p>
                    <button onClick={loadUsageData} className="refresh-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!usageData) {
        return (
            <div className="api-usage-analytics">
                <div className="no-data">No analytics data available</div>
            </div>
        );
    }

    const {cumulative, tier_breakdown} = usageData;

    // Tier colors
    const tierColors = {
        free: '#1976d2',
        basic: '#2e7d32',
        professional: '#f57c00',
        enterprise: '#7b1fa2'
    };

    const getTierColor = (tier) => {
        return tierColors[tier] || '#666';
    };

    const toggleUserExpansion = (userId) => {
        const newExpanded = new Set(expandedUsers);
        if (newExpanded.has(userId)) {
            newExpanded.delete(userId);
        } else {
            newExpanded.add(userId);
        }
        setExpandedUsers(newExpanded);
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="chart-tooltip">
                    <p className="tooltip-label">{label}</p>
                    <p className="tooltip-value">
                        {formatNumber(payload[0].value)} calls
                    </p>
                </div>
            );
        }
        return null;
    };


    return (
        <div className="api-usage-analytics">
            <div className="analytics-header">
                <h2>API Usage Analytics</h2>
                <div className="header-actions">
                    <button onClick={loadUsageData} className="refresh-button">
                        Refresh
                    </button>
                </div>
            </div>

            {/* Filter Controls */}
            <div className="analytics-filters">
                <div className="filter-group">
                    <label>Date Range:</label>
                    <select 
                        value={dateFilter} 
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="all">All time</option>
                        <option value="custom">Custom (Days)</option>
                    </select>
                    {dateFilter === "custom" && (
                        <input
                            type="number"
                            min="1"
                            value={customDays}
                            onChange={(e) => setCustomDays(e.target.value)}
                            className="custom-days-input"
                            placeholder="Enter number of days"
                        />
                    )}
                </div>
                
                <div className="filter-group">
                    <label>Plan Type:</label>
                    <select 
                        value={planTypeFilter} 
                        onChange={(e) => setPlanTypeFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Plans</option>
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="professional">Professional</option>
                        <option value="enterprise">Enterprise</option>
                    </select>
                </div>
                
                <div className="filter-group">
                    <label>Endpoint Category:</label>
                    <select 
                        value={endpointCategoryFilter} 
                        onChange={(e) => setEndpointCategoryFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Categories</option>
                        <option value="models">Models</option>
                        <option value="datasets">Datasets</option>
                        <option value="predictions">Predictions</option>
                        <option value="training">Training</option>
                    </select>
                </div>
            </div>

            {/* Summary Metrics */}
            <div className="metrics-horizontal">
                <div className="metric-box">
                    <div className="metric-box-label">Total API Calls</div>
                    <div className="metric-box-value">{formatNumber(cumulative.api_calls)}</div>
                    <div className="metric-box-subtitle">All time cumulative</div>
                </div>
                <div className="metric-box">
                    <div className="metric-box-label">Active Users</div>
                    <div className="metric-box-value">{filteredUsers.length}</div>
                    <div className="metric-box-subtitle">{planTypeFilter !== 'all' ? `${planTypeFilter} tier users` : 'Users with API usage'}</div>
                </div>
                <div className="metric-box">
                    <div className="metric-box-label">Models Created</div>
                    <div className="metric-box-value">{formatNumber(cumulative.models_created || 0)}</div>
                    <div className="metric-box-subtitle">Total models</div>
                </div>
                <div className="metric-box">
                    <div className="metric-box-label">Datasets Uploaded</div>
                    <div className="metric-box-value">{formatNumber(cumulative.datasets_uploaded || 0)}</div>
                    <div className="metric-box-subtitle">Total datasets</div>
                </div>
                {usageData?.model_types && (
                    <>
                        <div className="metric-box" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                            <div className="metric-box-label" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Neural Network Models</div>
                            <div className="metric-box-value" style={{ color: 'white' }}>{formatNumber(usageData.model_types.neural_network?.count || 0)}</div>
                            <div className="metric-box-subtitle" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                { // Make percentage of total models created (including unknown)
                                ((usageData.model_types.neural_network?.count || 0) / cumulative.models_created * 100).toFixed(1)}% of total
                            </div>
                        </div>
                        <div className="metric-box" style={{ background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)', color: 'white' }}>
                            <div className="metric-box-label" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Classical Models</div>
                            <div className="metric-box-value" style={{ color: 'white' }}>{formatNumber(usageData.model_types.classical?.count || 0)}</div>
                            <div className="metric-box-subtitle" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                {
                                    ((usageData.model_types.classical?.count || 0) / cumulative.models_created * 100).toFixed(1)}% of total
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Top 5 Users Widget */}
            {usageData?.top_users && usageData.top_users.length > 0 && (
                <div className="analytics-section">
                    <h3>Top 5 Users by API Usage</h3>
                    <div className="top-users-widget">
                        {usageData.top_users.map((user, index) => {
                            const maxCalls = usageData.top_users[0]?.api_calls || 1;
                            const percentage = (user.api_calls / maxCalls) * 100;
                            
                            return (
                                <div key={user.user_id} className="top-user-item">
                                    <div className="top-user-rank">
                                        <div className="rank-badge">#{index + 1}</div>
                                    </div>
                                    <div className="top-user-info">
                                        <div className="top-user-name">{user.full_name}</div>
                                        <div className="top-user-email">{user.email}</div>
                                    </div>
                                    <div className="top-user-tier">
                                        <span className={`tier-${user.subscription_tier.toLowerCase()}`}>
                                            {user.subscription_tier}
                                        </span>
                                    </div>
                                    <div className="top-user-stats">
                                        <div className="top-user-calls">{formatNumber(user.api_calls)}</div>
                                        <div className="top-user-calls-label">API Calls</div>
                                    </div>
                                    <div className="top-user-progress">
                                        <div 
                                            className="top-user-progress-fill" 
                                            style={{ 
                                                width: `${percentage}%`,
                                                backgroundColor: getTierColor(user.subscription_tier.toLowerCase())
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Tier Breakdown - Top */}
            <div className="analytics-section">
                <h3>Usage by Subscription Tier</h3>
                {Object.keys(tier_breakdown).length === 0 ? (
                    <div className="no-data">No tier breakdown data available</div>
                ) : (
                    <div className="chart-container" style={{ display: 'flex', alignItems: 'center', gap: '30px', overflow: 'visible' }}>
                        <div style={{ flexShrink: 0, padding: '30px 40px', overflow: 'visible', minWidth: '420px' }}>
                            <ResponsiveContainer width={420} height={380}>
                                <PieChart>
                                    <Pie
                                        data={tierChartData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, value, percent }) => `${name}\n${formatNumber(value)} (${(percent * 100).toFixed(0)}%)`}
                                        outerRadius={85}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {tierChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={getTierColor(entry.tier)} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        formatter={(value) => formatNumber(value)}
                                        contentStyle={{ borderRadius: '6px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div className="plan-distribution">
                                {Object.entries(tier_breakdown)
                                    .sort(([, a], [, b]) => b - a)
                                    .map(([tier, count]) => {
                                        const total = Object.values(tier_breakdown).reduce((sum, val) => sum + val, 0);
                                        const percentage = total > 0 ? (count / total) * 100 : 0;
                                        return (
                                            <div key={tier} className="plan-bar">
                                                <div className="plan-label">
                                                    <span className="plan-name">
                                                        <span className={`tier-${tier.toLowerCase()}`}>{tier}</span>
                                                    </span>
                                                    <span className="plan-count">{formatNumber(count)}</span>
                                                </div>
                                                <div className="plan-progress">
                                                    <div 
                                                        className="plan-fill"
                                                        style={{ 
                                                            width: `${percentage}%`,
                                                            background: `linear-gradient(90deg, ${getTierColor(tier.toLowerCase())}, ${getTierColor(tier.toLowerCase())}dd)`
                                                        }}
                                                    ></div>
                                                </div>
                                                <div className="plan-percentage">{percentage.toFixed(1)}%</div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Tier Usage Advanced Analytics */}
            {usageData?.tier_analytics && (
                <div className="tier-analytics-grid">
                    {Object.entries(usageData.tier_analytics).map(([tier, stats]) => (
                        <div 
                            key={tier} 
                            className="tier-analytics-card" 
                            style={{ borderLeft: `4px solid ${getTierColor(tier.toLowerCase())}` }} // Keep strictly dynamic styles inline
                        >
                            <h4 className="tier-analytics-title">
                                {tier} Tier
                            </h4>
                            
                            <div className="tier-analytics-metric">
                                <span className="metric-label">Avg Calls/User</span>
                                <span className="metric-value">{formatNumber(stats.average_api_calls)}</span>
                            </div>

                            <div className="tier-analytics-metric">
                                <span className="metric-label">P90 Usage</span>
                                <span className="metric-value">{formatNumber(stats.p90_api_calls)}</span>
                            </div>

                            <div className="tier-analytics-metric">
                                <span className="metric-label">Top Endpoint</span>
                                <span className="metric-value" style={{ fontFamily: 'monospace', fontSize: '13px', background: '#f8f9fa', padding: '2px 6px', borderRadius: '4px' }}>
                                    {stats.top_endpoint && stats.top_endpoint !== 'None' ? stats.top_endpoint : '-'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Daily Usage Trends */}
            <div className="analytics-section">
                <h3>Daily Usage</h3>
                {dailyChartData.length === 0 ? (
                    <div className="no-data">No daily usage data available</div>
                ) : (
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={dailyChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="date" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval="preserveStartEnd"
                                />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Line 
                                    type="monotone" 
                                    dataKey="calls" 
                                    stroke="#667eea" 
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                    activeDot={{ r: 6 }}
                                    name="API Calls"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Monthly Usage Trends */}
            <div className="analytics-section">
                <h3>Monthly Usage</h3>
                {monthlyChartData.length === 0 ? (
                    <div className="no-data">No monthly usage data available</div>
                ) : (
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={monthlyChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="month" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar 
                                    dataKey="calls" 
                                    fill="#667eea"
                                    name="API Calls"
                                    radius={[8, 8, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Endpoint Category Breakdown */}
            {usageData?.category_breakdown && usageData.category_breakdown.length > 0 && (
                <div className="analytics-section">
                    <h3>Usage by Endpoint Category</h3>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart data={usageData.category_breakdown}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="category" 
                                    angle={-15}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar 
                                    dataKey="api_calls" 
                                    fill="#764ba2"
                                    name="API Calls"
                                    radius={[8, 8, 0, 0]}
                                >
                                    {usageData.category_breakdown.map((entry, index) => {
                                        const colors = {
                                            models: '#667eea',
                                            datasets: '#2e7d32',
                                            predictions: '#f57c00',
                                            training: '#d32f2f',
                                            auth: '#1976d2',
                                            other: '#757575'
                                        };
                                        return (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={colors[entry.category] || '#764ba2'} 
                                            />
                                        );
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    
                    {/* Category Statistics Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '25px' }}>
                        {usageData.category_breakdown.map((cat) => {
                            const total = usageData.category_breakdown.reduce((sum, c) => sum + c.api_calls, 0);
                            const percentage = total > 0 ? (cat.api_calls / total * 100).toFixed(1) : 0;
                            const categoryIconClass = {
                                models: 'icon-models',
                                datasets: 'icon-datasets',
                                predictions: 'icon-predictions',
                                training: 'icon-training',
                                auth: 'icon-auth',
                                other: 'icon-other'
                            };
                            
                            return (
                                <div key={cat.category} className="category-stat-card">
                                    <div className={`category-icon ${categoryIconClass[cat.category] || 'icon-other'}`}></div>
                                    <div className="category-name">{cat.category.charAt(0).toUpperCase() + cat.category.slice(1)}</div>
                                    <div className="category-count">{formatNumber(cat.api_calls)}</div>
                                    <div className="category-percentage">{percentage}% of total</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Model Type Comparison (NN vs Classical) */}
            {usageData?.model_types && usageData.model_types.total > 0 && (
                <div className="analytics-section">
                    <h3>Model Type Distribution</h3>

                    {(() => {
                        // Calculate totals
                        const totalModels = cumulative.models_created || 0;
                        const nnCount = usageData.model_types.neural_network.count || 0;
                        const classicalCount = usageData.model_types.classical.count || 0;
                        const unknownCount = Math.max(totalModels - nnCount - classicalCount, 0);
                        // Helper to get accurate percentage of the total
                        const getPct = (val) => totalModels > 0 ? (val / totalModels * 100) : 0;

                        // Prepare Chart Data
                        const chartData = [
                            { name: 'Neural Network', value: nnCount, color: '#667eea' },
                            { name: 'Classical', value: classicalCount, color: '#2e7d32' }
                        ];
                        // Only add Unknown slice if it exists
                        if (unknownCount > 0) {
                            chartData.push({ name: 'Unknown/Legacy', value: unknownCount, color: '#9ca3af' });
                        }

                        if (totalModels === 0) return <div className="no-data">No models created yet</div>;

                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '40px', flexWrap: 'wrap' }}>
                                {/* PIE CHART */}
                                <div style={{ flex: '0 0 auto', minWidth: '350px' }}>
                                    <ResponsiveContainer width={350} height={300}>
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={true}
                                                label={({ name, value }) => 
                                                    `${name}: ${formatNumber(value)} (${getPct(value).toFixed(1)}%)`
                                                }
                                                outerRadius={100}
                                                dataKey="value"
                                            >
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => formatNumber(value)} contentStyle={{ borderRadius: '6px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* LEGEND / STATS LIST */}
                                <div style={{ flex: '1', minWidth: '300px' }}>
                                    <div className="model-type-comparison">
                                        
                                        {/* Neural Network */}
                                        <div className="model-type-item">
                                            <div className="model-type-header">
                                                <div className="model-type-icon" style={{ background: '#667eea' }}></div>
                                                <div>
                                                    <div className="model-type-name">Neural Network Models</div>
                                                    <div className="model-type-desc">Deep learning and neural architectures</div>
                                                </div>
                                            </div>
                                            <div className="model-type-stats">
                                                <div className="model-type-count">{formatNumber(nnCount)}</div>
                                                <div className="model-type-percentage">{getPct(nnCount).toFixed(1)}%</div>
                                            </div>
                                        </div>

                                        {/* Classical */}
                                        <div className="model-type-item">
                                            <div className="model-type-header">
                                                <div className="model-type-icon" style={{ background: '#2e7d32' }}></div>
                                                <div>
                                                    <div className="model-type-name">Classical Models</div>
                                                    <div className="model-type-desc">Traditional ML algorithms</div>
                                                </div>
                                            </div>
                                            <div className="model-type-stats">
                                                <div className="model-type-count">{formatNumber(classicalCount)}</div>
                                                <div className="model-type-percentage">{getPct(classicalCount).toFixed(1)}%</div>
                                            </div>
                                        </div>

                                        {/* Unknown / Legacy (Conditionally Rendered) */}
                                        {unknownCount > 0 && (
                                            <div className="model-type-item">
                                                <div className="model-type-header">
                                                    <div className="model-type-icon" style={{ background: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold' }}>?</div>
                                                    <div>
                                                        <div className="model-type-name">Legacy / Unknown</div>
                                                        <div className="model-type-desc">Models created before tracking update</div>
                                                    </div>
                                                </div>
                                                <div className="model-type-stats">
                                                    <div className="model-type-count">{formatNumber(unknownCount)}</div>
                                                    <div className="model-type-percentage">{getPct(unknownCount).toFixed(1)}%</div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="model-type-total">
                                            <strong>Total Models:</strong> {formatNumber(totalModels)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}
            {/* Usage by User - Bottom */}
            <div className="analytics-section">
                <h3>Usage by User</h3>
                {filteredUsers.length === 0 ? (
                    <div className="no-data">No user usage data available</div>
                ) : (
                    <div className="revenue-breakdown">
                        {filteredUsers.map((u) => {
                            const isExpanded = expandedUsers.has(u.user_id);
                            const dailyEntries = Object.entries(u.daily || {}).sort(([a], [b]) => a.localeCompare(b));
                            const monthlyEntries = Object.entries(u.monthly || {}).sort(([a], [b]) => a.localeCompare(b));
                            
                            return (
                                <div key={u.user_id} className="user-item">
                                    <div className="revenue-item usage-item-hover" onClick={() => toggleUserExpansion(u.user_id)} style={{ cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <strong>{u.full_name}</strong>
                                                <span className={`tier-${u.subscription_tier.toLowerCase()}`}>{u.subscription_tier}</span>
                                                <span style={{ fontSize: '12px', color: 'var(--text-muted, #999)' }}>
                                                    {isExpanded ? '▼' : '▶'}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '13px', color: 'var(--text-secondary, #666)' }}>
                                                {u.email}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div className="revenue-amount">{formatNumber(u.cumulative.api_calls)}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-muted, #999)', marginTop: '4px' }}>
                                                API Calls
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {isExpanded && (
                                        <div className="user-details">
                                            <div className="user-details-grid">
                                                <div className="user-details-section">
                                                    <h4>Daily Usage</h4>
                                                    {dailyEntries.length === 0 ? (
                                                        <div className="user-details-empty">No daily usage</div>
                                                    ) : (
                                                        <div className="user-details-list">
                                                            {dailyEntries.map(([date, count]) => (
                                                                <div key={date} className="user-details-item">
                                                                    <span className="user-details-item-date">{formatDate(date)}</span>
                                                                    <span className="user-details-item-value">{formatNumber(count)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="user-details-section">
                                                    <h4>Monthly Usage</h4>
                                                    {monthlyEntries.length === 0 ? (
                                                        <div className="user-details-empty">No monthly usage</div>
                                                    ) : (
                                                        <div className="user-details-list">
                                                            {monthlyEntries.map(([month, count]) => (
                                                                <div key={month} className="user-details-item">
                                                                    <span className="user-details-item-date">{formatMonth(month)}</span>
                                                                    <span className="user-details-item-value">{formatNumber(count)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="user-details-footer">
                                                <div>
                                                    <span className="user-details-footer-label">Models Created: </span>
                                                    <strong className="user-details-footer-value">{formatNumber(u.cumulative.models_created || 0)}</strong>
                                                </div>
                                                <div>
                                                    <span className="user-details-footer-label">Datasets Uploaded: </span>
                                                    <strong className="user-details-footer-value">{formatNumber(u.cumulative.datasets_uploaded || 0)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Analytics Footer */}
            <div className="analytics-footer">
                <small>
                    Last updated: {new Date().toLocaleString()}
                </small>
            </div>
        </div>
    );
};

export default APIUsageAnalytics;
